import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import {
  RetrieveAgency,
  RetrieveResponse,
} from '@interfaces/retrieve.interface';
import { firstValueFrom, Observable } from 'rxjs';

export interface Branding {
  logo: string;
  favicon: string;
  secondary: string;
  url: string;
  delegatorId: string;
  agencyId: string;
  name: string;
  footerHtml?: string | null;
  logoLink?: string | null;
  isWelo?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class BrandingServcice {
  private static readonly TOKEN_KEY = 'brandingToken';

  private readonly baseUrl = environment.apiUrl;
  private readonly apiKey = environment.xapikey;

  private http = inject(HttpClient);
  headers = new HttpHeaders()
    .set('Content-Type', 'application/json')
    .set('X-Api-Key', this.apiKey);

  getBrandingAgency(id: string | undefined): Observable<Branding> {
    return this.http.get<Branding>(`${this.baseUrl}/${id}/motor-b2c/branding`, {
      headers: this.headers,
    });
  }

  /** Branding de la agencia resuelto desde el token del deep-link de TC. */
  getBrandingByToken(token: string): Observable<Branding> {
    return this.http.get<Branding>(
      `${this.baseUrl}/motor-b2c/branding/by-token`,
      {
        headers: this.headers,
        params: { token },
      }
    );
  }

  /**
   * APP_INITIALIZER: en la entrada deep-link de Turismo City
   * (/booking-payments?token=...&solutionID0=...) no viene el agency id en
   * la URL, así que el branding se resuelve por token ANTES de que arranque
   * cualquier componente. Así LayoutComponent ya encuentra el branding en
   * sesión (stepper visible) y BaseService manda los headers de agencia
   * desde el primer request (pricing).
   */
  initFromEntryUrl(): Promise<void> {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('token');
    const solutionId = url.searchParams.get('solutionID0');
    if (token && solutionId) {
      return this.resolveFromToken(token);
    }

    // Vuelta de Mercado Pago (/confirmation/success|pending|failure) con la
    // sesión perdida (pestaña nueva, incógnito): sin branding no hay header
    // ni stepper. Se resuelve desde la agencia de la reserva.
    const isMpReturn = /^\/confirmation\/(success|pending|failure)/.test(
      url.pathname
    );
    if (isMpReturn && !sessionStorage.getItem('branding')) {
      const bookingId =
        this.readStoredBookingId() ||
        url.searchParams.get('external_reference');
      if (bookingId) {
        return this.resolveFromBooking(bookingId);
      }
    }
    return Promise.resolve();
  }

  /**
   * Resuelve el branding desde la agencia dueña de la reserva
   * (retrieve → bookingData.agency.id → branding de la agencia). Si el
   * endpoint de branding falla, arma un branding mínimo con los datos de la
   * agencia del retrieve: alcanza para stepper, header y headers de agencia
   * (el color cae al default).
   */
  async resolveFromBooking(bookingId: string): Promise<void> {
    let retrieve: RetrieveResponse;
    try {
      retrieve = await firstValueFrom(
        this.http.get<RetrieveResponse>(
          `${this.baseUrl}/transports/retrieve/${bookingId}`,
          { headers: this.retrieveHeaders() }
        )
      );
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('No se pudo recuperar la reserva para el branding:', e);
      return;
    }

    // Mismo camino que el deep-link: el retrieve trae el token de la
    // reserva y el branding se resuelve por token.
    if (retrieve.token) {
      await this.resolveFromToken(retrieve.token);
      if (sessionStorage.getItem('branding')) return;
    }

    // Último recurso: branding mínimo con los datos de la agencia.
    const agency = retrieve.bookingData?.agency;
    if (!agency?.delegatorId) return;
    const branding = this.minimalBrandingFromAgency(agency);
    sessionStorage.setItem('branding', JSON.stringify(branding));
    sessionStorage.setItem('id', JSON.stringify(agency.delegatorId));
  }

  private minimalBrandingFromAgency(agency: RetrieveAgency): Branding {
    return {
      logo: agency.imageProfile ?? '',
      favicon: '',
      secondary: '',
      url: '',
      delegatorId: agency.delegatorId,
      agencyId: agency.id,
      name: agency.agencyName ?? '',
      footerHtml: null,
      logoLink: null,
    };
  }

  /** Mismos headers que BaseService (agencia vacía si no hay branding). */
  private retrieveHeaders(): HttpHeaders {
    return this.headers
      .set('X-Delegatoragencyid', environment.delegatoragencyid)
      .set('X-Delegatorid', environment.delegatorid);
  }

  private readStoredBookingId(): string {
    try {
      return JSON.parse(sessionStorage.getItem('bookingId') || '""');
    } catch {
      return '';
    }
  }

  /** Nunca bloquea el arranque: ante error se sigue sin branding. */
  async resolveFromToken(token: string): Promise<void> {
    const alreadyResolved =
      sessionStorage.getItem(BrandingServcice.TOKEN_KEY) === token &&
      !!sessionStorage.getItem('branding');
    if (alreadyResolved) return;

    try {
      const branding = await firstValueFrom(this.getBrandingByToken(token));
      sessionStorage.setItem('branding', JSON.stringify(branding));
      // 'id' es el delegatorId: es lo que AppComponent usa para re-pedir el
      // branding (GET /{delegatorId}/motor-b2c/branding).
      sessionStorage.setItem('id', JSON.stringify(branding.delegatorId));
      sessionStorage.setItem(BrandingServcice.TOKEN_KEY, token);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('No se pudo resolver el branding por token:', e);
    }
  }
}
